import { useRef, useCallback, type ReactNode } from 'react';
import { useScrollFade } from '../hooks/useScrollFade';

type ScrollFadeContainerProps = {
    children: ReactNode;
    className?: string;
    fadeSize?: number;
    fadeOffset?: number;
    externalRef?: React.RefObject<HTMLDivElement | null>;
    style?: React.CSSProperties;
};

// Inject @property rules once so CSS can transition the custom properties
let injected = false;
function injectPropertyRules() {
    if (injected) return;
    injected = true;
    const style = document.createElement('style');
    style.textContent = `
        @property --scroll-fade-top {
            syntax: '<number>';
            inherits: false;
            initial-value: 0;
        }
        @property --scroll-fade-bottom {
            syntax: '<number>';
            inherits: false;
            initial-value: 0;
        }
    `;
    document.head.appendChild(style);
}

export function ScrollFadeContainer({
    children,
    className,
    fadeSize = 21,
    fadeOffset = 0,
    externalRef,
    style,
}: ScrollFadeContainerProps) {
    const internalRef = useRef<HTMLDivElement>(null);
    const { fadeTop, fadeBottom } = useScrollFade(externalRef ?? internalRef);

    injectPropertyRules();

    const mergedRef = useCallback(
        (node: HTMLDivElement | null) => {
            (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (externalRef) (externalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        },
        [externalRef],
    );

    const topSize = fadeTop ? fadeSize : 0;
    const botSize = fadeBottom ? fadeSize : 0;

    // When fadeOffset > 0, the top fade starts below the offset (e.g. padding area)
    // so it fades actual content rather than empty space.
    const topStart = fadeOffset ? `${fadeOffset}px` : '0px';
    const mask =
        `linear-gradient(to bottom, black ${topStart}, transparent ${topStart}, ` +
        `black calc(${topStart} + var(--scroll-fade-top) * 1px), ` +
        'black calc(100% - var(--scroll-fade-bottom) * 1px), transparent)';

    return (
        <div
            ref={mergedRef}
            className={className}
            style={
                {
                    ...style,
                    '--scroll-fade-top': topSize,
                    '--scroll-fade-bottom': botSize,
                    transition: '--scroll-fade-top 0.3s ease, --scroll-fade-bottom 0.3s ease',
                    maskImage: mask,
                    WebkitMaskImage: mask,
                } as React.CSSProperties
            }
        >
            {children}
        </div>
    );
}
